import React, { useEffect, useState, useRef } from 'react';
import {
  Plus, Users, Building2, RefreshCw,
  CheckCircle, XCircle, X, Network, Search
} from 'lucide-react';
import { partnerApi, Partner } from '../../partner/api/partner.api';
import PageInfoButton from '@/components/PageInfoButton';

interface CreateForm {
  name: string; contact_name: string; contact_email: string;
  contact_phone: string; website: string; notes: string;
}

const EMPTY: CreateForm = { name: '', contact_name: '', contact_email: '', contact_phone: '', website: '', notes: '' };

interface ChResult { title: string; company_number: string; address_snippet?: string; }

export default function Partners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateForm>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdNote, setCreatedNote] = useState<string | null>(null);

  // Companies House search state
  const [chQuery, setChQuery] = useState('');
  const [chResults, setChResults] = useState<ChResult[]>([]);
  const [chSearching, setChSearching] = useState(false);
  const chDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const res = await partnerApi.listPartners();
      setPartners(res.partners);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setError('Partner name is required'); return; }
    setSaving(true); setError(null);
    try {
      const res = await partnerApi.createPartner({
        name: form.name,
        contact_name: form.contact_name || undefined,
        contact_email: form.contact_email || undefined,
        contact_phone: form.contact_phone || undefined,
        website: form.website || undefined,
        notes: form.notes || undefined
      });
      setCreatedNote(`Partner "${res.partner.name}" created successfully.`);
      setForm({ ...EMPTY });
      setShowCreate(false);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(partner: Partner) {
    await partnerApi.updatePartner(partner.id, { is_active: !partner.is_active } as any);
    load();
  }

  function handleChQueryChange(q: string) {
    setChQuery(q);
    if (chDebounce.current) clearTimeout(chDebounce.current);
    if (!q.trim()) { setChResults([]); return; }
    chDebounce.current = setTimeout(async () => {
      setChSearching(true);
      try {
        const res = await fetch(`/api/companies/companies-house/search?q=${encodeURIComponent(q)}`, { credentials: 'include' });
        const data = await res.json();
        setChResults((data.items || data.results || []).slice(0, 6));
      } catch { setChResults([]); }
      finally { setChSearching(false); }
    }, 350);
  }

  function applyChResult(r: ChResult) {
    setForm(f => ({ ...f, name: r.title }));
    setChQuery('');
    setChResults([]);
  }

  return (
    <div className="p-6 space-y-6 max-w-screen-xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Partner Organisations</h1><PageInfoButton /></div>
          <p className="text-sm text-slate-500 mt-1">Partners manage their own portfolio of client companies within EIS</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Partner
        </button>
      </div>

      {createdNote && (
        <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800">
          <CheckCircle className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600" />
          <div className="flex-1">
            <p className="font-medium">{createdNote}</p>
            <p className="mt-1 text-emerald-700">
              Next: go to{' '}
              <a href="/admin/users" className="underline font-medium">Users → Add New User</a>
              , select the <strong>Partner Admin</strong> permission profile, and choose this partner from the dropdown.
            </p>
          </div>
          <button onClick={() => setCreatedNote(null)} className="p-0.5 hover:bg-emerald-100 rounded">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <div className="bg-white rounded-3xl border border-slate-100 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Create Partner Organisation</h3>
            <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
          </div>
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}

          {/* Companies House search */}
          <div className="relative">
            <label className="block text-xs font-medium text-slate-700 mb-1 flex items-center gap-1">
              <Search className="w-3 h-3" /> Search Companies House (optional auto-fill)
            </label>
            <div className="relative">
              <input
                type="text"
                value={chQuery}
                onChange={e => handleChQueryChange(e.target.value)}
                placeholder="Start typing a company name…"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 pr-8"
              />
              {chSearching && <RefreshCw className="absolute right-2.5 top-2.5 w-3.5 h-3.5 animate-spin text-slate-400" />}
            </div>
            {chResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg text-sm overflow-hidden">
                {chResults.map(r => (
                  <button
                    key={r.company_number}
                    type="button"
                    onClick={() => applyChResult(r)}
                    className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-slate-100 last:border-0"
                  >
                    <span className="font-medium text-slate-900">{r.title}</span>
                    {r.address_snippet && <span className="ml-2 text-xs text-slate-400">{r.address_snippet}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form onSubmit={handleCreate} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Organisation Name *</label>
              <input type="text" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="e.g. GreenPath Consulting Ltd" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Contact Name</label>
              <input type="text" value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Contact Email</label>
              <input type="email" value={form.contact_email} onChange={e => setForm(f => ({...f, contact_email: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
              <input type="tel" value={form.contact_phone} onChange={e => setForm(f => ({...f, contact_phone: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Website</label>
              <input type="url" value={form.website} onChange={e => setForm(f => ({...f, website: e.target.value}))}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                placeholder="https://..." />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none" />
            </div>
            <div className="col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setShowCreate(false)}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-60">
                {saving ? 'Creating...' : 'Create Partner'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Partners list */}
      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : partners.length === 0 ? (
        <div className="py-16 bg-white rounded-3xl border border-slate-100">
          <Network className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">No partners yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {partners.map(p => (
            <div key={p.id} className="bg-white rounded-3xl border border-slate-100 p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-slate-900">{p.name}</h3>
                  {p.contact_name && <p className="text-xs text-slate-500 mt-0.5">{p.contact_name}</p>}
                  {p.contact_email && <p className="text-xs text-blue-600 mt-0.5">{p.contact_email}</p>}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${p.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {p.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  {p.company_count} companies
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-slate-400" />
                  {p.user_count} users
                </span>
              </div>
              <div className="flex items-center justify-between pt-1 border-t border-slate-100">
                <span className="text-xs text-slate-400 font-mono">ID: {p.id}</span>
                <button
                  onClick={() => toggleActive(p)}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                >
                  {p.is_active ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
                  {p.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Onboarding hint */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 flex items-start gap-3">
        <Network className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-slate-800 mb-1">To give a partner admin access:</p>
          <p>Create the partner organisation above, then go to{' '}
            <a href="/admin/users" className="text-violet-600 hover:underline font-medium">Users → Add New User</a>.
            Select the <strong>Partner Admin</strong> permission profile — a partner dropdown will appear automatically.
            partner admins log in and manage their own portfolio.
          </p>
        </div>
      </div>
    </div>
  );
}
